/**
 * Error handling utilities for suppressing harmless browser errors
 *
 * ResizeObserver loop errors occur when ResizeObserver callbacks take longer than a single animation frame.
 * These errors are harmless and can be safely ignored in production.
 */

/**
 * Suppresses ResizeObserver loop errors which are harmless but can clutter console
 * These errors occur when ResizeObserver callbacks take longer than a single animation frame
 */
export const suppressResizeObserverErrors = (): void => {
  if (typeof window === "undefined") return;

  // Store the original error handler
  const originalError = window.console.error;
  const originalWarn = window.console.warn;

  // Only override if not already overridden
  if ((window.console.error as any).__resizeObserverPatched) {
    return;
  }

  const wrappedError = (...args: any[]) => {
    // Check if this is a ResizeObserver loop error
    const isResizeObserverError = args.some((arg) => {
      if (typeof arg === "string") {
        return (
          arg.includes("ResizeObserver loop") ||
          arg.includes(
            "ResizeObserver loop completed with undelivered notifications",
          ) ||
          arg.includes("ResizeObserver loop limit exceeded")
        );
      }
      if (arg instanceof Error) {
        return (
          arg.message.includes("ResizeObserver") ||
          arg.message.includes("loop completed")
        );
      }
      return false;
    });

    // If it's a ResizeObserver loop error, suppress it (it's harmless)
    if (!isResizeObserverError) {
      originalError.apply(window.console, args);
    } else {
      // Optionally log to debug in development
      if (process.env.NODE_ENV === "development") {
        originalWarn.apply(window.console, [
          "[Suppressed] ResizeObserver loop error (harmless):",
          ...args,
        ]);
      }
    }
  };

  // Mark as patched to prevent multiple patches
  wrappedError.__resizeObserverPatched = true;
  window.console.error = wrappedError;
};

/**
 * Sets up global error handlers for the application
 */
export const setupGlobalErrorHandlers = (): void => {
  // Suppress ResizeObserver loop errors
  suppressResizeObserverErrors();

  // Handle unhandled promise rejections
  if (typeof window !== "undefined") {
    const originalAddEventListener = window.addEventListener;

    // Patch addEventListener to catch ResizeObserver errors early
    window.addEventListener = function (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions,
    ) {
      if (type === "error") {
        const wrappedListener = (event: ErrorEvent) => {
          const errorMessage = event.error?.message || event.message || "";
          // Check for ResizeObserver errors
          if (
            errorMessage.includes("ResizeObserver") ||
            errorMessage.includes("loop completed") ||
            errorMessage.includes("loop limit exceeded")
          ) {
            event.preventDefault();
            if (process.env.NODE_ENV === "development") {
              console.warn(
                "[Suppressed] ResizeObserver error in global error handler:",
                event.error || event.message,
              );
            }
            return;
          }

          // Call original listener if it's a function
          if (typeof listener === "function") {
            listener.call(this, event);
          } else if (listener && typeof listener.handleEvent === "function") {
            listener.handleEvent(event);
          }
        };

        return originalAddEventListener.call(
          this,
          type,
          wrappedListener as EventListener,
          options,
        );
      }

      if (type === "unhandledrejection") {
        const wrappedListener = (event: PromiseRejectionEvent) => {
          const errorMessage =
            event.reason?.message || event.reason?.toString() || "";
          // Check for ResizeObserver errors
          if (
            errorMessage.includes("ResizeObserver") ||
            errorMessage.includes("loop completed")
          ) {
            event.preventDefault();
            if (process.env.NODE_ENV === "development") {
              console.warn(
                "[Suppressed] ResizeObserver error in unhandled rejection:",
                event.reason,
              );
            }
            return;
          }

          // Call original listener if it's a function
          if (typeof listener === "function") {
            listener.call(this, event);
          } else if (listener && typeof listener.handleEvent === "function") {
            listener.handleEvent(event);
          }
        };

        return originalAddEventListener.call(
          this,
          type,
          wrappedListener as EventListener,
          options,
        );
      }

      return originalAddEventListener.call(this, type, listener, options);
    };
  }
};

/**
 * Restores original console.error function
 */
export const restoreConsoleError = (): void => {
  if (typeof window === "undefined") return;

  // Find the original console.error by looking through the prototype chain
  const consoleProto = Object.getPrototypeOf(window.console);
  if (consoleProto && consoleProto.error) {
    window.console.error = consoleProto.error;
  }
};

/**
 * Debounce function to limit how often a function can be called
 * Useful for resize events that trigger ResizeObserver
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

/**
 * Throttle function to limit how often a function can be called
 * Useful for resize events that trigger ResizeObserver
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number,
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

const errorHandler = {
  suppressResizeObserverErrors,
  setupGlobalErrorHandlers,
  restoreConsoleError,
  debounce,
  throttle,
};

export default errorHandler;
